// 2) Third-party libraries
import { filter, get, isEmpty, toLower } from "lodash-es";
import { all, call, delay, fork, put, takeLatest } from "redux-saga/effects";

// 3) Local imports
// 3.1 Constants
import { API_NAME, maxAPIRefetchCount, refetchAPIDelay } from "../../config/config";
import { USERS_PAGE } from "../../config/tableAndPageConstants";

// 3.3 Project-specific modules � components/types/other
import client, { post, get as amplifyGet } from "src/api";
import { _queries as queries } from "../../graphql/queries.graphql";

// 3.4 Utility functions / types
import { checkShouldRequestRefetch } from "../../utils/commonFunctions";
import { DynamicObject } from "../../utils/commonInterfaces";

// 3.5 Redux actions / store
import type { ApplicationState } from "../storeTypes";
import {
    changeUsersRoleRequestAction,
    changeUsersRoleResponseAction,
    getUserByEmailAction,
    getUserByNameAction,
    getUserByNameAndEmailAction,
    getUserLastLoginDateAction,
    getUserPreferencesRequestAction,
    getUserPreferencesSuccessAction,
    getUsersErrorAction,
    getUsersRequestAction,
    getUsersSuccessAction,
    removeUsersRequestAction,
} from "./userSlice";

let refetchCount = 0;
/**
 * Function that connects to get users and role for company api.
 */
function* handleGetUsersRequest({ payload }: any) {
    try {
        // To call async functions, use redux-saga's `call()`.
        const { filters, sortBy, sortAscending, pageSize, currentPage } =
            payload;

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_USERS_AND_ROLE_FOR_COMPANY,
                variables: {
                    Name: filters.Name,
                    Email: filters.Email,
                    IsIgnoredCustomerPortalUsers: filters.IsIgnoredCustomerPortalUsers,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * USERS_PAGE.pageSize,
                    RoleIds: filters.Role,
                }
            }
        );

        const { CompanyUserRoles } = get(res.data, 'GetUsersForCompany');
        const responsePayload = {
            data: CompanyUserRoles,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(CompanyUserRoles.length < pageSize) &&
                    !(pageSize < USERS_PAGE.pageSize),
            },
        };

        refetchCount = 0;

        yield put(getUsersSuccessAction(responsePayload));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getUsersRequestAction(payload));
        } else {
            const errorMessage =
                'Error fetching user list. Please try again later.';

            yield put(getUsersErrorAction([errorMessage]));
        }
    }
}

/**
 * Function that connects to change user roles api.
 */
function* handleChangeUsersRoleRequest({ payload: sagaPayload }: any) {
    const { filter, userIds, roleId, excludeUsers, callback } = sagaPayload;
    const payload = {
        Filter: filter,
        RoleId: roleId,
        UserIds: userIds,
        ExcludeUsers: excludeUsers,
    };

    try {
        // To call async functions, use redux-saga's `call()`.
        yield call(post, {
            apiName: API_NAME,
            path: '/user/role/change',
            options: {
                body: payload
            }
        });

        yield put(changeUsersRoleResponseAction());

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        yield put(changeUsersRoleResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that connects to get users and role and returns a user record based on name. Name checking should be equals instead of contains.
 */
function* handleGetUserByName({ payload }: any) {
    const { name, callback } = payload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_USERS_AND_ROLE_FOR_COMPANY,
                variables: {
                    Name: name,
                    Email: '',
                    SortField: 'Name',
                    Ascending: true,
                    PageSize: 10,
                    Skip: 0,
                }
            }
        );

        refetchCount = 0;
        const { CompanyUserRoles } = get(res.data, 'GetUsersForCompany');

        const filteredUser = get(
            filter(CompanyUserRoles, (userRole: DynamicObject) => {
                const givenName = get(userRole, 'User.GivenName');
                const familyName = get(userRole, 'User.FamilyName');

                if (givenName || familyName) {
                    const userFullName = `${givenName} ${familyName}`;

                    if (userFullName === name) {
                        return true;
                    }
                }

                return false;
            }),
            0
        );

        const response = {
            IsSuccess: !isEmpty(filteredUser),
            User: get(filteredUser, 'User'),
        };

        if (callback) {
            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getUserByNameAction(name, callback));
        } else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.body) {
                    returnData = JSON.parse((err as any).response.body);
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: ['An unknown error occurred.'] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
            }
        }
    }
}

/**
 * Function that connects to get users and role and returns a user record based on email. Email checking should be equals instead of contains.
 */
function* handleGetUserByEmail({ payload }: any) {
    const { email, callback } = payload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_USERS_AND_ROLE_FOR_COMPANY,
                variables: {
                    Name: '',
                    Email: email,
                    SortField: 'Name',
                    Ascending: true,
                    PageSize: 10,
                    Skip: 0,
                }
            }
        );

        refetchCount = 0;

        const { CompanyUserRoles } = get(res.data, 'GetUsersForCompany');
        const filteredUser = get(
            filter(CompanyUserRoles, (userRole: DynamicObject) => {
                const userEmail = get(userRole, 'User.Email');

                if (userEmail && toLower(userEmail) === toLower(email)) {
                    return true;
                }

                return false;
            }),
            0
        );

        const response = {
            IsSuccess: !isEmpty(filteredUser),
            User: get(filteredUser, 'User'),
        };

        if (callback) {
            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getUserByEmailAction(email, callback));
        } else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.body) {
                    returnData = JSON.parse((err as any).response.body);
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: ['An unknown error occurred.'] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
            }
        }
    }
}

/**
 * Function that connects to get users and role and returns a user record based on name/email.
 */
function* handleGetUserByNameAndEmail({ payload }: any) {
    const { inputValue, callback } = payload;
    try {
        // To call async functions, use redux-saga's `call()`.
        const nameRes: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_USERS_AND_ROLE_FOR_COMPANY,
                variables: {
                    Name: inputValue,
                    Email: '',
                    SortField: 'Name',
                    Ascending: true,
                    PageSize: 10,
                    Skip: 0,
                }
            }
        );

        const emailRes: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_USERS_AND_ROLE_FOR_COMPANY,
                variables: {
                    Name: '',
                    Email: inputValue,
                    SortField: 'Name',
                    Ascending: true,
                    PageSize: 10,
                    Skip: 0,
                }
            }
        );

        refetchCount = 0;

        const { CompanyUserRoles:CompanyUserRolesByName  } = get(nameRes.data, 'GetUsersForCompany');
        const { CompanyUserRoles:CompanyUserRolesByEmail  } = get(emailRes.data, 'GetUsersForCompany');
        const combinedCompanyUserRoles = [...CompanyUserRolesByName, ...CompanyUserRolesByEmail];
        const uniqueUsersMap = new Map<string, any>();
        
        combinedCompanyUserRoles.forEach(role => {
            const user = role.User;
            const userId = user.UserId;
        
            if (userId && !uniqueUsersMap.has(userId)) {
                uniqueUsersMap.set(userId, user);
            }
        });
        
        const uniqueUsers = Array.from(uniqueUsersMap.values());

        const response = {
            IsSuccess: !isEmpty(uniqueUsers),
            User: uniqueUsers,
        };

        if (callback) {
            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getUserByNameAndEmailAction(inputValue, callback));
        } else {
            if (callback) {
                const returnData = get(err.response, 'data')
                    ? err.response.data
                    : { Messages: [err.message] };
                returnData.IsSuccess = false;
                callback(returnData);
            }
        }
    }
}

/**
 * Function that connects to remove users api.
 */
function* handleRemoveUsersRequest({ payload: sagaPayload }: any) {
    const { filter, userIds, excludeUsers, callback } = sagaPayload;
    const payload = {
        Filter: filter,
        UserIds: userIds,
        ExcludeUsers: excludeUsers,
    };

    try {
        // To call async functions, use redux-saga's `call()`.
        yield call(post, {
            apiName: API_NAME,
            path: '/company/removeuser',
            options: {
                body: payload
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that connects to fetching of last successful login by user.
 */
function* handleGetUserLastLoginDate({ payload }: any) {
    const { callback } = payload;
    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(amplifyGet,
            {
                apiName: API_NAME,
                path: '/productannouncement/userlastlogindatetime'
            }
        );

        const res: DynamicObject = yield body.json();
        const response = {
            ...res,
            IsSuccess: true,
        };

        if (callback) {
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that connects to get users preferences api.
 */
function* handleGetUserPreferencesRequest({ payload }: any) {
    const { callback } = payload;
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_USER_PREFERENCE,
                variables: {}
            }
        );

        const UserPreference = get(res.data, 'GetUserPreference');

        refetchCount = 0;
        yield put(getUserPreferencesSuccessAction(UserPreference));

        if (callback) {
            callback({
                IsSuccess: true,
                data: get(UserPreference, 'Preferences', []),
            });
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getUserPreferencesRequestAction(callback));
        } else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.body) {
                    returnData = JSON.parse((err as any).response.body);
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: ['An unknown error occurred.'] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
            }
        }
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetUsersRequest() {
    yield takeLatest(
        getUsersRequestAction.type,
        handleGetUsersRequest
    );
}

function* watchChangeUsersRoleRequest() {
    yield takeLatest(
        changeUsersRoleRequestAction.type,
        handleChangeUsersRoleRequest
    );
}

function* watchGetUserByName() {
    yield takeLatest(
        getUserByNameAction.type,
        handleGetUserByName
    );
}

function* watchGetUserByEmail() {
    yield takeLatest(
        getUserByEmailAction.type,
        handleGetUserByEmail
    );
}

function* watchGetUserByNameAndEmail() {
    yield takeLatest(
        getUserByNameAndEmailAction.type,
        handleGetUserByNameAndEmail
    );
}

function* watchRemoveUsersRequest() {
    yield takeLatest(
        removeUsersRequestAction.type,
        handleRemoveUsersRequest
    );
}

function* watchGetUserLastLoginDate() {
    yield takeLatest(
        getUserLastLoginDateAction.type,
        handleGetUserLastLoginDate
    );
}

function* watchGetUserPreferencesRequest() {
    yield takeLatest(
        getUserPreferencesRequestAction.type,
        handleGetUserPreferencesRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* usersSaga() {
    yield all([
        fork(watchGetUsersRequest),
        fork(watchChangeUsersRoleRequest),
        fork(watchGetUserByName),
        fork(watchGetUserByEmail),
        fork(watchGetUserByNameAndEmail),
        fork(watchRemoveUsersRequest),
        fork(watchGetUserLastLoginDate),
        fork(watchGetUserPreferencesRequest),
    ]);
}

export default usersSaga;
